// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
// Parser dispatch surface: builds the shared hook bundle, instantiates
// the extracted-text parser registries (raw + envelope), and exposes
// per-format entry points (Garmin user log / TXI, JPI CSV / binary) plus
// the generic parseFlightText / parseFlightTextEnvelope dispatchers.
//
// Exposed as a factory because inferInsightPhase is defined in app.js.

import { assignFlightPhases } from "../analysis/phase-detection.js";
import {
  elapsedSecondsFromClockRows,
  finalizeParsedPoints,
  resamplePointsToOneHz,
  suppressAuxDropoutRows,
  suppressImplausibleCarbTemp,
  suppressImplausibleOilTemps,
} from "../parsers/data-prep.js";
import { mapGarminRowsWithDeps } from "../parsers/garmin.js";
import {
  createExtractedParserEnvelopeRegistry,
  createExtractedParserRegistry,
  JPI_BINARY_PARSER_VERSION,
} from "../parsers/index.js";
import { parseJpiBinaryDetailedWithDeps, parseJpiBinaryWithDeps } from "../parsers/jpi.js";
import { createCanonicalParserEnvelope } from "../core/schema.js";

export function createParserDispatch({ inferInsightPhase }) {
  function mapGarminRows(rows) {
    return mapGarminRowsWithDeps(rows, { assignFlightPhases });
  }

  function extractedTextParserHooks() {
    return {
      finalizeParsedPoints,
      resamplePointsToOneHz,
      assignFlightPhases,
      inferInsightPhase,
      suppressAuxDropoutRows,
      suppressImplausibleCarbTemp,
      suppressImplausibleOilTemps,
      elapsedSecondsFromClockRows,
    };
  }

  function extractedTextParserRegistry() {
    return createExtractedParserRegistry(extractedTextParserHooks());
  }

  function extractedTextParserEnvelopeRegistry() {
    return createExtractedParserEnvelopeRegistry(extractedTextParserHooks(), {
      parserConfidence: "Medium",
      parserWarnings: [],
    });
  }

  function parseGarminUserLogCsv(text, entry = {}) {
    return extractedTextParserRegistry().get("garmin-user-log")(text, entry);
  }

  function parseGarminTxiCsv(text) {
    return extractedTextParserRegistry().get("garmin-txi")(text);
  }

  function parseJpiCsv(text) {
    return extractedTextParserRegistry().get("jpi-csv")(text);
  }

  function parseJpiBinary(arrayBuffer, entry = {}) {
    return parseJpiBinaryWithDeps(arrayBuffer, entry, extractedTextParserHooks());
  }

  function parseJpiBinaryEnvelope(arrayBuffer, entry = {}) {
    const parsed = parseJpiBinaryDetailedWithDeps(arrayBuffer, entry, extractedTextParserHooks());
    return createCanonicalParserEnvelope(parsed.points, {
      parserId: "jpi-binary",
      parserVersion: JPI_BINARY_PARSER_VERSION,
      parserConfidence: "Medium",
      parserWarnings: parsed.parserWarnings,
      engineLayout: parsed.engineLayout,
      sourceData: parsed.sourceData,
    });
  }

  function parseFlightText(entry, text) {
    const extractedParser = extractedTextParserRegistry().get(entry.parser);
    if (extractedParser) return extractedParser(text, entry);
    throw new Error(`${entry.monitor || "This"} log format is not supported yet`);
  }

  function parseFlightTextEnvelope(entry, text) {
    const extractedParser = extractedTextParserEnvelopeRegistry().get(entry.parser);
    if (extractedParser) return extractedParser(text, entry);
    return createCanonicalParserEnvelope(parseFlightText(entry, text), {
      parserId: entry.parser,
      parserVersion: `${entry.parser}/v1`,
      parserConfidence: "Medium",
      parserWarnings: [],
    });
  }

  return {
    mapGarminRows,
    extractedTextParserHooks,
    extractedTextParserRegistry,
    extractedTextParserEnvelopeRegistry,
    parseGarminUserLogCsv,
    parseGarminTxiCsv,
    parseJpiCsv,
    parseJpiBinary,
    parseJpiBinaryEnvelope,
    parseFlightText,
    parseFlightTextEnvelope,
  };
}
